import { completeFilter, type FilterCondition } from '@crm/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { CompanyDetail } from '../components/companies/company-detail.tsx';
import { CompanyDialog } from '../components/companies/company-dialog.tsx';
import { FilterControls } from '../components/record-table/filter-controls.tsx';
import {
  RecordTable,
  type ColumnDef,
  type RecordSource,
} from '../components/record-table/index.ts';
import { Gate } from '../components/shell/gate.tsx';
import { Button } from '../components/ui/button.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../components/ui/sheet.tsx';
import { describeError } from '../lib/api.ts';
import { api, type CompanyView } from '../lib/client.ts';
import { flagForCountry } from '../lib/countries.ts';
import { useVisibleDefinitions } from '../lib/custom-fields.ts';
import { companyFilterFields } from '../lib/list-filters.ts';
import { andFilter, flatConditions, useListView } from '../lib/list-view.ts';
import { useMembers } from '../lib/record-data.ts';
import { useSession } from '../lib/session.tsx';
import { useSettings } from '../lib/settings.ts';

/** The API's filter-DSL search, newest first; the search box matches name and domain. */
function companiesSource(
  organisationId: string,
  conditions: FilterCondition[],
): RecordSource<CompanyView> {
  // An unfilled row stays on screen but is never sent: the API answers 400 to it.
  const filter = completeFilter(conditions.length ? { and: conditions } : undefined);
  return {
    key: `companies:${organisationId}:${JSON.stringify(filter ?? null)}`,
    sortable: [],
    async fetchPage({ cursor, limit, filter: text }) {
      const page = await api.companies.search({
        limit: Math.min(limit, 200),
        ...(text.trim() ? { q: text.trim() } : {}),
        ...(filter ? { filter } : {}),
        ...(cursor ? { cursor } : {}),
      });
      return { items: page.items, nextCursor: page.nextCursor };
    },
  };
}

/**
 * Companies (TS-1750): the list; a row opens the company in a side panel (`?open=<id>`, so it
 * can be linked to) with its form. A project has exactly one company, so companies live here.
 */
export function CompaniesPage() {
  const session = useSession();
  const qc = useQueryClient();
  const [settings] = useSettings(session.me.user.id);
  const { members, nameOf } = useMembers();
  const definitions = useVisibleDefinitions('companies');
  const filterFields = useMemo(
    () => companyFilterFields({ definitions, members, search: api.companies.search }),
    [definitions, members],
  );
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const search = useSearch({ from: '/authed/companies' });
  const openId = search.open ?? null;
  const setOpenId = useCallback(
    (id: string | null) =>
      void navigate({
        to: '/companies',
        search: (prev) => {
          const { open: _o, ...rest } = prev;
          return id ? { ...rest, open: id } : rest;
        },
        replace: true,
      }),
    [navigate],
  );
  // Filters, search and columns in `?view=` beside `open`, remembered like the other lists.
  const write = useCallback(
    (view: string) =>
      void navigate({ to: '/companies', search: (prev) => ({ ...prev, view }), replace: true }),
    [navigate],
  );
  const { state, setState } = useListView({
    entity: null,
    remember: 'companies',
    encoded: search.view,
    write,
  });
  const conditions = useMemo(() => flatConditions(state.filter), [state.filter]);
  const setConditions = useCallback(
    (next: FilterCondition[]) => setState({ ...state, filter: andFilter(next) }),
    [setState, state],
  );
  const source = useMemo(
    () => companiesSource(session.me.organisation.id, conditions),
    [session.me.organisation.id, conditions],
  );
  const columns = useMemo<ColumnDef<CompanyView, unknown>[]>(
    () => [
      { accessorKey: 'name', header: 'Name', size: 240 },
      { id: 'website', header: 'Website', size: 220, accessorFn: (c) => c.website ?? '—' },
      { id: 'industry', header: 'Industry', size: 180, accessorFn: (c) => c.industry ?? '—' },
      {
        id: 'country',
        header: 'Country',
        size: 160,
        accessorFn: (c) => c.country ?? '—',
        cell: ({ row }) => {
          const country = row.original.country;
          const flag = flagForCountry(country);
          return country ? `${flag ? `${flag} ` : ''}${country}` : '—';
        },
      },
      { id: 'owner', header: 'Owner', size: 180, cell: ({ row }) => nameOf(row.original.ownerId) },
    ],
    [nameOf],
  );
  const open = useQuery({
    queryKey: ['company', openId],
    queryFn: () => api.companies.get(openId!),
    enabled: openId !== null,
  });
  const refresh = (id?: string) =>
    Promise.all(
      [['records'], ['companies'], ...(id ? [['company', id]] : [])].map((queryKey) =>
        qc.invalidateQueries({ queryKey }),
      ),
    );
  return (
    <Gate permission="customer.read" feature="customers.enabled">
      <PageHeader
        title="Companies"
        description="The client companies behind each project."
        actions={
          session.can('customer.create') && (
            <Button onClick={() => setCreating(true)}>
              <Plus /> New company
            </Button>
          )
        }
      />
      <RecordTable
        columns={columns}
        source={source}
        getRowId={(c) => c.id}
        mode={settings.pagination}
        pageSize={settings.pageSize}
        filterPlaceholder="Search companies…"
        toolbar={
          <FilterControls fields={filterFields} conditions={conditions} onChange={setConditions} />
        }
        tableState={state}
        onTableStateChange={setState}
        emptyMessage="No companies yet."
        onRowClick={(c) => setOpenId(c.id)}
      />
      <Sheet open={openId !== null} onOpenChange={(o) => !o && setOpenId(null)}>
        {openId !== null && (
          <SheetContent data-testid="company-panel">
            <SheetTitle className="sr-only">Company</SheetTitle>
            <SheetDescription className="sr-only">
              Details of the selected company.
            </SheetDescription>
            {open.data ? (
              <CompanyDetail
                key={`${open.data.id}:${open.data.updatedAt}`}
                company={open.data}
                onSaved={async () => {
                  await refresh(open.data.id);
                }}
              />
            ) : open.error != null ? (
              <p className="text-danger text-sm" role="alert">
                {describeError(open.error)}
              </p>
            ) : (
              <p className="text-muted-foreground text-sm">Loading…</p>
            )}
          </SheetContent>
        )}
      </Sheet>
      {creating && (
        <CompanyDialog
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await refresh();
          }}
        />
      )}
    </Gate>
  );
}
